import { Box, Grid } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/** API, UTIL*/
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';

/** STYLED */
import ComponentApi from 'api/components-api';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import Loading from 'component/Loading';
import DropdownButton from 'new-components/buttons/dropdown-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import { getBranch } from 'utils/app.utils';
import ExportToExcel from './ExportToExcel';
import NewPatientList from './NewPatientList';


interface AccessChannel {
    accessChannelId: number;
    channelName: string;

}
export default function Newpatient() {
    const { t } = useTranslation();
    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);
    const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const momentDate = () => momentNowRange

    const [branchId, setBranchId] = useState<number>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rangeDate, setRangeDate] = useState<string[]>(momentDate);

    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');

    const [newPatientData, setNewPatientData] = useState<any[]>([]);
    const [newPatientDataExport, setNewPatientDataExport] = useState<any[]>([]);
    const [newPatientPage, setNewPatientPage] = useState<number>(1);
    const [newPatientTypePage, setNewPatientTypePage] = useState<number>(1);
    const [newPatientPageLimit, setNewPatientPageLimit] = useState<number>(10);
    const [accessChannels, setAccessChannels] = useState([]);
    const [selectAccessChannel, setSelectedAccessChannel] = useState({ accessChannelId: 0, channelName: t(`REPORT.FILTER.ALL`) })
    const [accessChannelId, setAccessChannelId] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(false);
    const filter = [
        branchId === 0 ? t(`REPORT.FILTER.ALL`) : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
        rangeDate,
        accessChannelId === 0 ? t(`REPORT.FILTER.ALL`) : `${_.get(_.find(accessChannels, { accessChannelId: accessChannelId }), 'channelName', _.get(_.find(accessChannels, { accessChannelId: accessChannelId }), 'channelNameEn'))}`
    ]

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'NEW_PATIENT') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        return true
    }

    const loadAccessChannels = useCallback((searchValue?: string) => {
        ComponentApi.findAllAccessChannels()
            .then(({ data: dataAccessChannels }) => { setAccessChannels(dataAccessChannels) })
            .catch((e) => setLoading(false))
            .finally(() => {
                setLoading(false)
            })
    }, []);

    useEffect(() => {
        loadAccessChannels()
        verifyPermission()
    }, [branchId])

    const exportExcel = async () => {
        setLoading(true);
        try {

            // Fetch latest data before export
            // const exportData = await fetchNewPatientExport();
            await ExportToExcel({
                data: newPatientDataExport,
                branches,
                branchId,
                filter,
                filterDateStart: rangeDate[0],
                filterDateEnd: rangeDate[1],
                snType: true,
                t,
            })
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        } finally {
            setLoading(false);
        }
    }
    const fetchNewPatientExport = async () => {
        try {
            setLoading(true)
            await ReportApi.reportNewPatient({
                page: 1,
                pageLimit: 1000,
                export: 1,
                branchId: branchId,
                dateStart: rangeDate[0],
                dateEnd: rangeDate[1],
                accessChannelsId: accessChannelId === 0 ? null : accessChannelId,
            })
                .then(({ headers, data }) => {
                    setNewPatientDataExport(data)
                    setLoading(false)
                }).catch(() => setLoading(false))
        } catch (err) {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchNewPatientExport()
    }, [newPatientPage, newPatientPageLimit])

    const fetchNewPatient = async () => {
        try {
            setLoading(true)
            await ReportApi.reportNewPatient({
                page: newPatientPage,
                pageLimit: newPatientPageLimit,
                branchId: branchId,
                dateStart: rangeDate[0],
                dateEnd: rangeDate[1],
                accessChannelsId: accessChannelId === 0 ? null : accessChannelId,
            })
                .then(({ headers, data }) => {
                    setNewPatientData(data)
                    setRowCount(headers['x-total'])
                    setLoading(false)
                }).catch((e) => setLoading(false))
        } catch (err) {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchNewPatient()
    }, [newPatientPage, newPatientPageLimit])

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }
    const handleChangeRowsPerPage = (limit: number) => setNewPatientPageLimit(limit)
    const handleChangePage = (val: number) => setNewPatientPage(val)

    return (
        <Box>
            <Loading show={loading} />
            <HeaderCard text={t(`REPORT.REPORT_NAME.NEW_PATIENT`)} />
            <Box padding="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3}>
                        <SelectInput
                            onChange={(e) => {
                                setBranchId(e.target.value)
                                setNewPatientPage(1)
                            }}
                            options={_.map(branches, (item) => ({ value: item.branchId, name: item.branchName }))}
                            value={branchId}
                            defaultValue={`${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn', '')} `}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate || emptyRangePicker}

                            onchange={(val: any) => {
                                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])

                                setNewPatientPage(1)
                            }}
                            label={''}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <SelectInput
                            onChange={(e) => {
                                setAccessChannelId(e.target.value)
                                setNewPatientPage(1)
                            }}
                            options={[{ value: 0, name: t('REPORT.FILTER.ALL') }, ..._.map(accessChannels, (item: any) => ({ value: item.accessChannelId, name: item.channelName }))]}
                            value={accessChannelId}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                onClick={() => {
                                    fetchNewPatient()
                                    fetchNewPatientExport()
                                    setNewPatientPage(1)
                                    setLoading(true)
                                }}
                                type={'primary'}
                                variant={'contained'}
                                style={{ width: '120px' }}
                            />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    exportExcel();
                                } else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);

                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box paddingTop="16px">
                    <NewPatientList
                        page={newPatientPage}
                        pageLimit={newPatientPageLimit}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={newPatientData}
                        dataExport={newPatientDataExport}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                        headCellsSeconds={filter}
                        date={rangeDate}
                    />
                </Box>
            </Box>
        </Box>
    )
}
